/**
 * conde-shop-by-catalog.js
 * Shared catalog availability matrix for the Shop-By guided flow.
 *
 * Public API on window.CondeShopByCatalog:
 *   - load() -> Promise<Variant[]>
 *   - getAvailableValues(dimension, selection) -> Set<string>
 *   - findVariant(selection) -> { handle, variantId } | null
 *   - findUniqueProducts(selection) -> [{ handle, variantId }]
 *   - DIMENSIONS, ORDER, lengthFromOption, slug
 */

(function () {
  'use strict';

  function localPath(path) {
    var routes = window.Shopify && window.Shopify.routes;
    var root = (routes && routes.root) || document.documentElement.dataset.rootUrl || '/';
    return root.replace(/\/$/, '') + path;
  }


  var CACHE_KEY = 'conde-shop-by-catalog-v1';
  var CACHE_TTL_MS = 60 * 60 * 1000;
  var PAGE_SIZE = 250;
  var DIMENSIONS = ['method', 'category', 'texture', 'length'];

  function slug(s) {
    return String(s || '').toLowerCase().trim().replace(/\s+/g, '-');
  }

  function lengthFromOption(option1) {
    if (!option1) return null;
    var match = String(option1).match(/(\d+)/);
    return match ? match[1] : null;
  }

  function tagValue(tags, prefix) {
    var arr = Array.isArray(tags) ? tags : String(tags || '').split(',');
    for (var i = 0; i < arr.length; i++) {
      var t = String(arr[i]).trim().toLowerCase();
      if (t.indexOf(prefix) === 0) return t.substring(prefix.length);
    }
    return null;
  }

  function normalizeProducts(products) {
    var rows = [];
    products.forEach(function (p) {
      var method = tagValue(p.tags, 'type:');
      var category = tagValue(p.tags, 'category:');
      if (!method) return;
      (p.variants || []).forEach(function (v) {
        var length = lengthFromOption(v.option1);
        var texture = slug(v.option2);
        if (!length || !texture) return;
        rows.push({
          method: method,
          category: category,
          length: length,
          texture: texture,
          handle: p.handle,
          variantId: String(v.id),
          available: !!v.available
        });
      });
    });
    return rows;
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || !Array.isArray(parsed.data)) return null;
      if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (e) {
      // ignore quota errors
    }
  }

  function fetchAllProducts() {
    var page = 1;
    var all = [];
    function next() {
      return fetch(localPath('/products.json?page=') + page + '&limit=' + PAGE_SIZE)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var batch = (data && data.products) || [];
          all = all.concat(batch);
          if (batch.length === PAGE_SIZE) {
            page++;
            return next();
          }
          return all;
        });
    }
    return next();
  }

  var loadPromise = null;

  function load() {
    if (loadPromise) return loadPromise;
    var nocache = (function () {
      try {
        return new URLSearchParams(window.location.search).get('nocache') === '1';
      } catch (e) { return false; }
    })();
    if (!nocache) {
      var cached = readCache();
      if (cached) {
        loadPromise = Promise.resolve(cached);
        return loadPromise;
      }
    }
    loadPromise = fetchAllProducts().then(function (products) {
      var rows = normalizeProducts(products);
      writeCache(rows);
      return rows;
    }).catch(function (err) {
      loadPromise = null; // allow retry
      throw err;
    });
    return loadPromise;
  }

  function matchesSelection(row, selection) {
    for (var k in selection) {
      if (!Object.prototype.hasOwnProperty.call(selection, k)) continue;
      var v = selection[k];
      if (v == null || v === '') continue;
      if (row[k] !== v) return false;
    }
    return true;
  }

  function getAvailableValues(rows, dimension, selection) {
    var partial = {};
    DIMENSIONS.forEach(function (d) {
      if (d !== dimension && selection[d]) partial[d] = selection[d];
    });
    var values = new Set();
    for (var i = 0; i < rows.length; i++) {
      if (matchesSelection(rows[i], partial)) {
        var v = rows[i][dimension];
        if (v) values.add(v);
      }
    }
    return values;
  }

  function findVariant(rows, selection) {
    for (var i = 0; i < rows.length; i++) {
      if (matchesSelection(rows[i], selection)) {
        return { handle: rows[i].handle, variantId: rows[i].variantId };
      }
    }
    return null;
  }

  function findUniqueProducts(rows, selection) {
    var seen = {};
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      if (!matchesSelection(rows[i], selection)) continue;
      if (seen[rows[i].handle]) continue;
      seen[rows[i].handle] = true;
      out.push({ handle: rows[i].handle, variantId: rows[i].variantId });
    }
    return out;
  }

  window.CondeShopByCatalog = {
    DIMENSIONS: DIMENSIONS.slice(),
    ORDER: DIMENSIONS.slice(),
    slug: slug,
    lengthFromOption: lengthFromOption,
    load: load,
    getAvailableValues: function (dimension, selection) {
      return load().then(function (rows) { return getAvailableValues(rows, dimension, selection || {}); });
    },
    findVariant: function (selection) {
      return load().then(function (rows) { return findVariant(rows, selection || {}); });
    },
    findUniqueProducts: function (selection) {
      return load().then(function (rows) { return findUniqueProducts(rows, selection || {}); });
    }
  };
})();
